// ── Company detail ("/companies/:id") — the company record (Wave 1) ─────────
// The full company record: an identity header (name + status + reference) over
// decomposed sections (linked Programs / Cohorts), each owning its OWN
// honest-state read. The header read routes through useHonestQuery ->
// <HonestState>; a gate / permission-denied / unavailable / error NEVER reads as
// an empty company shell, and a not-found (the 400 "was not found" faked 404 ->
// "empty" posture) renders an honest "Company not found", NEVER a cross-record
// leak.
//
// THE ID IS A STRING — the route param is the bigint-safe company id, passed
// VERBATIM (never Number()'d). A bad/absent id can't be a real record, so the
// page sends it back to the list rather than firing a doomed read.
//
// ROLE-GATING: Super Admin / Admin can Edit + Archive from the header; Company
// Admin is read-only (no write affordances); denied roles never reach the route
// (the route manifest excludes them) and a denied READ renders permission-denied.

import { Badge, Button, DetailSurface, useToast } from "@vjal/ui";
import { useState } from "react";
import { Link } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { destructiveButtonClass } from "../../components/ui-classes";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { useOrganizationLifecycleMutations } from "../../hooks/useOrganizationLifecycleMutations";
import { evaluateActionCapability } from "../../domain/capabilities";
import { liveRecordIdFromPath } from "../product-live-rows";
import type { CoachingCompanyDto } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { CompanyFormModal, type CompanyFormValues } from "./company-form-modal";
import { CompanyArchiveModal } from "./company-archive-modal";
import { CompanyProgramsSection, CompanyCohortsSection } from "./company-linked-section";
import { companyDisplayName, companyStatusLabel, isValidCompanyId } from "./company-detail-helpers";

const services = getCoachingServices();

export function CompanyDetailPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const mutations = useOrganizationLifecycleMutations();
  const allowUpdate = evaluateActionCapability(role, "companies:update").allowed;

  const companyId = liveRecordIdFromPath(routePath, "companies");
  const validId = isValidCompanyId(companyId);

  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const query = useHonestQuery<CoachingCompanyDto>(
    coachingQueryKeys.companyDetail(companyId ?? ""),
    () => services.companies.detail(companyId as string),
    { enabled: validId }
  );
  const company = query.data ?? null;

  function submitEdit(values: CompanyFormValues) {
    if (!company) return Promise.reject(new Error("No company loaded"));
    return mutations.updateCompany
      .mutateAsync({ companyId: company.id, body: { name: values.name, status: values.status } })
      .then((updated) => {
        setEditOpen(false);
        mutations.updateCompany.reset();
        toast.success(`Company ${updated.name ?? values.name} updated`);
        return updated;
      });
  }

  function submitArchive() {
    if (!company) return Promise.reject(new Error("No company loaded"));
    return mutations.updateCompany
      .mutateAsync({ companyId: company.id, body: { name: company.name, status: "archived" } })
      .then((updated) => {
        setArchiveOpen(false);
        mutations.updateCompany.reset();
        toast.success(`Company ${updated.name ?? company.name} archived`);
        return updated;
      });
  }

  // ── Bad/absent id, or a not-found / non-ready posture → one honest body. ────
  if (!validId || query.posture !== null || !company) {
    const posture = !validId ? "empty" : (query.posture ?? "loading");
    const notFound = posture === "empty";
    return (
      <section aria-label="Company detail" className="col-span-12" data-testid="company-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture={posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={notFound ? "Company not found" : undefined}
            message={
              notFound
                ? "This company doesn't exist, or it isn't in your workspace. It may have been removed."
                : undefined
            }
            canCreate={notFound}
            createAction={
              notFound ? (
                <Link to="/companies" className="text-link no-underline hover:underline">
                  Back to companies
                </Link>
              ) : undefined
            }
          />
        </div>
      </section>
    );
  }

  // ── Ready: render the company record ────────────────────────────────────────
  const archived = company.status === "archived";
  return (
    <section aria-label="Company detail" className="col-span-12 flex flex-col gap-4" data-testid="company-detail-page">
      {/* F3: real @vjal/ui DetailSurface for Detail-1 company-detail IR. */}
      <DetailSurface
        className="min-w-0 max-w-full [&>div>div>div:first-child]:flex-wrap [&>div>div>div:first-child]:gap-y-2 [&_h2]:min-w-0"
        title={
          <span className="flex min-w-0 max-w-full flex-wrap items-center gap-2 break-words sm:gap-3">
            {companyDisplayName(company.name)}
            <Badge variant={archived ? "warning" : "success"}>{companyStatusLabel(company.status)}</Badge>
          </span>
        }
        actions={
          allowUpdate ? (
            <div
              className="flex w-full min-w-0 max-w-full flex-wrap justify-end gap-2 sm:w-auto sm:flex-1"
              data-testid="company-detail-actions"
              role="group"
              aria-label="Company actions"
            >
              <Button variant="secondary" onClick={() => setEditOpen(true)} aria-label="Edit company">
                Edit company
              </Button>
              <Button
                variant="danger"
                className={destructiveButtonClass}
                onClick={() => setArchiveOpen(true)}
                disabled={archived}
                aria-label="Open archive confirmation"
              >
                Archive
              </Button>
            </div>
          ) : undefined
        }
        fields={[
          {
            id: "back",
            label: "Directory",
            value: (
              <Link to="/companies" className="text-link no-underline hover:underline">
                ← All companies
              </Link>
            ),
          },
          {
            id: "reference",
            label: "Reference",
            value: company.id,
            mono: true,
          },
          {
            id: "status",
            label: "Status",
            value: companyStatusLabel(company.status),
          },
        ]}
        titleExactName="Company title">
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <CompanyProgramsSection companyId={company.id} />
          <CompanyCohortsSection companyId={company.id} />
        </div>
      </DetailSurface>

      {allowUpdate && (
        <>
          <CompanyFormModal
            open={editOpen}
            mode="edit"
            company={company}
            onClose={() => {
              setEditOpen(false);
              mutations.updateCompany.reset();
            }}
            onSubmit={submitEdit}
            submitting={mutations.updateCompany.isPending}
            submitError={mutations.updateCompany.error}
          />
          <CompanyArchiveModal
            open={archiveOpen}
            company={company}
            onClose={() => {
              setArchiveOpen(false);
              mutations.updateCompany.reset();
            }}
            onConfirm={submitArchive}
            submitting={mutations.updateCompany.isPending}
            submitError={mutations.updateCompany.error}
          />
        </>
      )}
    </section>
  );
}
